import { useState, Fragment, useEffect } from "react";
import dynamic from "next/dynamic";

import { dehydrate, QueryClient, useQueries } from '@tanstack/react-query';
import { getQueryFn, queryKeyManager } from "../../utils/api/queryFunction";
import queryConfig from "../../utils/api/queryConfig";
import { hasProperty } from "../../utils/validate";
import { addDataLayerEvent } from "../../utils/data.layer.utils/data.layer.util";
import DefaultBanner from "../../components/Shared/DefaultBanner";
import { BasicImage } from "../../utils/page-requests/feature-page";

const SEO = dynamic(() => import("../../components/Shared/SEO"));
const ProductLayout = dynamic(() => import("../../components/Layout/ProductLayout"));
// const SmallBanner = dynamic(() => import('../../components/Shared/Banners/SmallBanner'));
const ProductCard = dynamic(() => import('../../components/Shared/Cards/ProductCard'));
const FilterNav = dynamic(() => import("../../components/Shared/Filters/FilterNav"));

type PowerItem = {
	category: string,
	slug: string,
	title: string,
	featuredImage: string,
	bannerTitle: string,
	bannerDescription: string,
	url: string,
}

const getPower = (data: PowerItem[], selCategory: string) => {
	if (selCategory !== '' && selCategory !== 'all') {
		return data.filter(m => {
			return m.category === selCategory
		});
	} else {
		return data;
	}
}

const getValsForShowroomGTMEvent = data => {
	return {
		category: data.category,
		url: data.url,
		title: 'Power - Showroom',
	};
};

const ShowroomPage = props => {
	const { token, category } = props;

	const [pageDataRes, categoriesDataRes] = useQueries({
		queries: [
			{
				queryKey: queryKeyManager(["power", "products"], ["list", category]),
				queryFn: () => getQueryFn("power/list", token),
				...queryConfig
			},
			{
				queryKey: queryKeyManager(["power", "products"], ["categories"]),
				queryFn: () => getQueryFn(`category/powerProducts`, ""),
				...queryConfig
			}
		]
	});

	if (pageDataRes.isLoading || categoriesDataRes.isLoading) {
		return <span />
	}

	const pageData = pageDataRes.data;
	const categories = categoriesDataRes.data;

	useEffect(() => {
		addDataLayerEvent("showroomPageView", getValsForShowroomGTMEvent(pageData));
	}, [pageData]);

	const products = getPower(pageData.power, category);

	return (
		<Fragment>
			<SEO seo={pageData.seo} />
			<ProductLayout nav={pageData.nav.header.links} logo={pageData.nav.header.logos.power} root="power" footer={pageData.nav.footer}>
				{/* <SmallBanner banner={pageData.banner} /> */}
				<DefaultBanner banner={pageData.banner} />
				<FilterNav
					nav={categories.data}
					navTitle={''}
					navAll={pageData.navAll}
					allRoute={"/power/showroom"}
					active={category}
				/>

				<div className="customContainer listCarProduct">
					<div className="row">
						{(!products || products.length === 0) && <div className="errorMessage">No products matched the criteria.</div>}
						{products.map((power, index) => {
							return (
								<div className="col s12 m6 l4" key={index}>
									<ProductCard
										key={index}
										productType={`power`}
										slug={power.slug}
										hasButton={false}
										title={power.title}
										img={power.featuredImage}
										heading={power.bannerTitle}
										description={power.bannerDescription}
										url={power.url}
									/>
								</div>
							)
						})}
					</div>
				</div>
			</ProductLayout>
		</Fragment>
	);
};

ShowroomPage.getInitialProps = async ({ query, req }) => {
	const { token, category = 'all' } = query;

	const queryClient = new QueryClient();

	await queryClient.prefetchQuery({
		queryKey: queryKeyManager(["power", "products"], ["list", category]),
		queryFn: () => getQueryFn("power/list", token),
		...queryConfig
	});

	await queryClient.prefetchQuery({
		queryKey: queryKeyManager(["power", "products"], ["categories"]),
		queryFn: () => getQueryFn(`category/powerProducts`, ""),
		...queryConfig
	});

	return {
		token: token,
		category,
		dehydratedState: dehydrate(queryClient)
	};
};

export default ShowroomPage;